"use client";

import { useTranslations } from "next-intl";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PhoneInput } from "@/components/ui/phone-input";
import type { Value } from "react-phone-number-input";
import type { ShippingAddress, ShippingCity } from "@/types";

interface ShippingAddressFieldsProps {
  isLoadingCities: boolean;
  cities: ShippingCity[];
  formData: ShippingAddress;
  formErrors: Record<string, string>;
  onInputChange: (field: keyof ShippingAddress, value: string | number) => void;
}

export function ShippingAddressFields({
  isLoadingCities,
  cities,
  formData,
  formErrors,
  onInputChange,
}: ShippingAddressFieldsProps) {
  const t = useTranslations();

  if (isLoadingCities) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="customerName">
          {t("checkout.shipping.customerName")} *
        </Label>
        <Input
          id="customerName"
          value={formData.customerName}
          onChange={(e) => onInputChange("customerName", e.target.value)}
          placeholder={t("checkout.shipping.customerNamePlaceholder")}
          className={formErrors.customerName ? "border-destructive" : ""}
        />
        {formErrors.customerName && (
          <p className="text-sm text-destructive">{formErrors.customerName}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="mobile">{t("checkout.shipping.mobile")} *</Label>
        <PhoneInput
          id="mobile"
          value={(formData.mobile || "") as Value}
          onChange={(val) => onInputChange("mobile", val ?? "")}
          placeholder="5XXXXXXXXX"
          className={formErrors.mobile ? "border-destructive" : ""}
        />
        {formErrors.mobile && (
          <p className="text-sm text-destructive">{formErrors.mobile}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">
          {t("checkout.shipping.email")} ({t("common.optional")})
        </Label>
        <Input
          id="email"
          type="email"
          value={formData.email || ""}
          onChange={(e) => onInputChange("email", e.target.value)}
          placeholder="email@example.com"
          dir="ltr"
          className={formErrors.email ? "border-destructive" : ""}
        />
        {formErrors.email && (
          <p className="text-sm text-destructive">{formErrors.email}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="city">{t("checkout.shipping.city")} *</Label>
        <Select
          value={formData.cityId ? String(formData.cityId) : ""}
          onValueChange={(value) => onInputChange("cityId", Number(value))}
        >
          <SelectTrigger
            className={formErrors.cityId ? "border-destructive" : ""}
          >
            <SelectValue placeholder={t("checkout.shipping.selectCity")} />
          </SelectTrigger>
          <SelectContent position="item-aligned" className="z-[220] max-h-60">
            {cities.map((city: any) => {
              const cid = city.id ?? city.Id;
              const label =
                city.nameAr || city.Name || city.name || city.NameEn || "";
              return (
                <SelectItem key={cid} value={String(cid)}>
                  {label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {formErrors.cityId && (
          <p className="text-sm text-destructive">{formErrors.cityId}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="address">{t("checkout.shipping.address")} *</Label>
        <Input
          id="address"
          value={formData.address}
          onChange={(e) => onInputChange("address", e.target.value)}
          placeholder={t("checkout.shipping.addressPlaceholder")}
          className={formErrors.address ? "border-destructive" : ""}
        />
        {formErrors.address && (
          <p className="text-sm text-destructive">{formErrors.address}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="details">
          {t("checkout.shipping.details")} ({t("common.optional")})
        </Label>
        <Textarea
          id="details"
          value={formData.details || ""}
          onChange={(e) => onInputChange("details", e.target.value)}
          placeholder={t("checkout.shipping.detailsPlaceholder")}
          rows={3}
        />
      </div>
    </>
  );
}
